import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, OnInit, Output} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map, merge,
  Observable,
  of,
  shareReplay, startWith,
  switchMap,
  tap
} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  BokkzAlgDataService,
  BokkzClient,
  BokkzVpbDataService,
  BokkzVpbLogic,
  CorporateTaxEntity,
  FeatureServiceComponent, IncomeTaxEntity,
  MenuInterface,
  VpbAangifte,
  VpbBerekening,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule, NgStyle} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-corporate-list',
  templateUrl: './feature-tax-corporate-list.component.html',
  styleUrls: ['./feature-tax-corporate-list.component.scss'],
  imports: [
    CommonModule,
    SharedUiTableModule,
    SharedUiButtonModule,
    NgStyle,
    FeatureServiceComponent
  ]
})
export class FeatureTaxCorporateListComponent implements OnInit {

  @Output() public details = new EventEmitter<string>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataService = inject(BokkzVpbDataService);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly vpbService = inject(BokkzVpbLogic);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly options$ = new BehaviorSubject<MenuInterface[]>([]);

  protected tableConfig!: TableConfiguration<any, any>;
  protected clientId = '';

  private lastTableStateKey: string | null = null;
  public showTable = false;
  public year = 0;
  public options: MenuInterface[] = [];
  public berekening = {} as VpbBerekening;
  public aangifteVorigJaar = {} as VpbAangifte;
  public berekeningVorigJaar = {} as VpbBerekening;
  public winstCorrecties = 0;
  public winstCorrectiesVorigJaar = 0;
  public saldoFiscaleWinstberekening = 0;
  public belastbaarBedrag = 0;
  public verschuldigdeVennootschapsbelasting: number | string = 0;
  public verschuldigdeVennootschapsbelastingVorigjaar: number | string = 0;
  public belastbaarBedragVorigjaar: number | string = '-';
  public saldoFiscaleWinstberekeningVorigjaar: number | string = '-';
  public isDefAangifte = false;
  public naamVennootschap = '';
  public dialogVisible = false;
  public activePage = '';
  private dispatchMenuTimer: ReturnType<typeof setTimeout> | null = null;

  readonly clientId$ = this.activatedRoute.params.pipe(
    map((params) => params['clientId'] ?? ''),
    filter((clientId) => !!clientId),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly year$ = this.algDataService.selectedYear.pipe(
    filter((year): year is number => year != null),
    distinctUntilChanged(),
    tap((year) => {
      this.year = year;
    }),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly corporateTaxLoad$ = combineLatest({
    clientId: this.clientId$,
    year: this.year$,
    reload: this.reload$,
  }).pipe(
    switchMap(({clientId, year}) =>
      this.bokkzClient.getCorporateTax(clientId, String(year)).pipe(
        tap((value) => {
          this.clientId = clientId;
          this.dataService.updateCorporateTaxEntity(value);
        }),
        catchError(() => of(null)),
      ),
    ),
    shareReplay({bufferSize: 1, refCount: true}),
  );


  readonly incomeTaxEntity$ = merge(this.corporateTaxLoad$, this.dataService.corporateTaxEntity).pipe(
    filter((entity): entity is CorporateTaxEntity => !!entity?.sstRelationId || !!entity?.aangifte),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  readonly isDefAangifte$: Observable<boolean> =
    this.dataService.isDefAangifte.pipe(
      tap(isDefAangifte => {
        this.isDefAangifte = isDefAangifte;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening.pipe(
      filter((data): data is VpbBerekening => this.hasResolvedVpbBerekening(data)),
      tap(value => {
        this.winstCorrecties = (value.belastbareWinst?.saldoFiscaleWinst || 0) - (value.belastbareWinst?.belastbareWinst || 0);
        this.belastbaarBedrag = value.belastbaarBedrag?.belastbaarBedrag || 0;
        this.saldoFiscaleWinstberekening = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening || 0;
        this.verschuldigdeVennootschapsbelasting = value.belasting?.verschuldigdeVennootschapsbelasting || 0;
        this.berekening = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbAangifteVergelijking$: Observable<VpbAangifte> =
    this.dataService.vpbAangifteVergelijking.pipe(
      filter((data): data is VpbAangifte => !!data),
      tap(value => {
        this.aangifteVorigJaar = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekeningVergelijking$: Observable<VpbBerekening> =
    this.dataService.vpbBerekeningVergelijking.pipe(
      filter((data): data is VpbBerekening => this.hasResolvedVpbBerekening(data)),
      tap(value => {
        this.berekeningVorigJaar = value;
        const fiscaleWinstBerekeningVorigJaar = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening || 0;
        this.belastbaarBedragVorigjaar = value.belastbaarBedrag?.belastbaarBedrag || '-';
        this.winstCorrectiesVorigJaar = fiscaleWinstBerekeningVorigJaar - (value.belastbaarBedrag?.belastbaarBedrag || 0);
        this.saldoFiscaleWinstberekeningVorigjaar = value.jaarstukken?.resultaat?.saldoFiscaleWinstberekening?.toLocaleString() || '-';
        this.verschuldigdeVennootschapsbelastingVorigjaar = value.belasting?.verschuldigdeVennootschapsbelasting?.toLocaleString() || '-';
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly tableVm$ = this.corporateTaxLoad$.pipe(
    switchMap((entity) => {
      if (!entity?.aangifte) {
        this.resetSums();
        this.dataService.updateVpbBerekening(null as any);
        this.dataService.aantalValidaties.next(0);
        this.dataService.menuInterface.next([]);
        this.options$.next([]);

        return of({
          entity: null,
          options: [] as MenuInterface[],
          tableConfig: this.createTableConfig([]),
        });
      }

      this.dataService.updateMenuInterface(entity.aangifte!);
      const options = this.dataService.menuInterface.value ?? [];
      this.options$.next([...options]);

      return this.dataService.calculateLatestCorporateTaxReturn().pipe(
        tap((berekening) => this.dataService.updateVpbBerekening(berekening)),
        tap(() => this.vpbService.getValidation(entity).subscribe()),
        map(() => ({
          entity,
          options,
          tableConfig: this.createTableConfig(options),
        })),
        startWith({
          entity,
          options,
          tableConfig: this.createTableConfig(options),
        }),
      );
    }),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.dispatchMenuTimer) {
        clearTimeout(this.dispatchMenuTimer);
        this.dispatchMenuTimer = null;
      }
    });

    this.dataService.corporateTaxEntity.pipe(debounceTime(0),
      filter((entity): entity is CorporateTaxEntity => !!entity?.aangifte),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((entity) => {
      this.dataService.updateMenuInterface(entity.aangifte!);
    });

    this.tableVm$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vm) => {
      this.options = vm.options;

      const tableStateKey = vm.entity?.id != null ? `data-${vm.entity.id}-${this.year}` : `empty-${this.year}`;

      if (this.lastTableStateKey !== tableStateKey) {
        this.lastTableStateKey = tableStateKey;

        this.showTable = false;
        this.tableConfig = undefined as any;
        this.cdr.detectChanges();

        this.tableConfig = vm.tableConfig;
        this.showTable = true;
        this.cdr.detectChanges();
      } else {
        this.tableConfig = vm.tableConfig;
        this.showTable = true;
      }
    });

    this.dataService.menuInterface.pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef)).subscribe((menuInterface) => {
      const old = this.options$.value;
      const next = [...(menuInterface ?? [])];

      if (JSON.stringify(old) === JSON.stringify(next)) {
        return;
      }

      this.options$.next(next);
      this.options = next;
      this.tableConfig = this.createTableConfig(next);
      this.cdr.detectChanges();

      for (let i = 0; i < next.length; i++) {
        if (JSON.stringify(old[i]) !== JSON.stringify(next[i])) {
          dispatchSavedItemEvent({
            identifier: next[i].value,
            result: next[i] as unknown as Record<number, MenuInterface>,
          });

          if (!next[i].visible) {
            dispatchDeletedItemEvent({
              identifier: next[i].value,
            });
          }
        }
      }

      this.cdr.detectChanges();
    });

    this.isDefAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbBerekening$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbAangifteVergelijking$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vpbBerekeningVergelijking$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public onAangifteCreated(): void {
    this.reload$.next();
  }

  private hasResolvedVpbBerekening(data: VpbBerekening | null | undefined): data is VpbBerekening {
    return !!(data?.jaarstukken || data?.belasting || data?.belastbaarBedrag || data?.belastbareWinst);
  }

  private createTableConfig(options: MenuInterface[]): TableConfiguration<any, any> {
    const criteria = new SearchCriteria();

    return TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria,

      search: () => {
        const sortedGroups = options
          .filter((item) => item.visible)
          .map((group) => ({
            ...group,
            subonderdelen: (group.subonderdelen || []).filter((sub) => sub.visible).sort((a, b) => a.value - b.value),
          }))
          .sort((a, b) => a.value - b.value);

        return of(new SearchResult(sortedGroups, sortedGroups.length));
      },
      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen aangifte gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'De aangifte vennootschapsbelasting wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe aangifte aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe aangifte`,
        buttonConfig: this.year < 2024 ? undefined : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  }

  public navigateToSubPage(pageMapping: string): void {
    const url = '/tax-corporate/' + 'modules/' + this.clientId + '/' + pageMapping;
    this.router.navigate([url]).then(() => console.log('complete'));
    this.activePage = pageMapping;
  }

  private resetSums(): void {
    this.winstCorrecties = 0;
    this.winstCorrectiesVorigJaar = 0;
    this.saldoFiscaleWinstberekening = 0;
    this.belastbaarBedrag = 0;
    this.verschuldigdeVennootschapsbelasting = 0;
    this.verschuldigdeVennootschapsbelastingVorigjaar = 0;
    this.belastbaarBedragVorigjaar = 0;
    this.saldoFiscaleWinstberekeningVorigjaar = 0;
  }
}
